// Site Configuration
// This file contains all the site-specific variables that must be set via environment variables

export interface SiteConfig {
  siteName: string;
  siteSection: string;
  fullTitle: string;
  api: {
    base: string;
    workspace: string;
    initiative: string;
  };
  logo: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  meta: {
    description: string;
    keywords: string[];
    ogImage: string;
    favicon: string;
  };
  footer: {
    copyrightName: string;
    copyrightLink: string;
  };
  seo: {
    canonicalBase: string;
    mainSiteUrl: string;
  };
  theme: {
    fonts: {
      sans: string;
      heading: string;
    };
    colors: {
      primary: string;
      primaryLight: string;
      primaryDark: string;
      secondary: string;
      accent: string;
      recovery: string;
    };
    backgrounds: {
      primary: string;
      secondary: string;
      dark: string;
      gradient: string;
    };
    text: {
      primary: string;
      secondary: string;
      light: string;
      muted: string;
    };
  };
}

// Helper function to get required environment variable
const getRequiredEnv = (key: string): string => {
  const value = import.meta.env[key]
  if (!value) {
    throw new Error(`Environment variable ${key} is required`)
  }
  return value
}

// Helper function to get optional environment variable with fallback
const getOptionalEnv = (key: string, fallback: string): string => {
  return import.meta.env[key] || fallback
}

// Helper function to parse keywords
const parseKeywords = (keywords: string): string[] => {
  return keywords.split(',').map(k => k.trim()).filter(Boolean);
}

// Function to get site configuration based on environment variables
export function getSiteConfig(): SiteConfig {
  // Required environment variables
  const siteName = getRequiredEnv('VITE_SITE_NAME')
  const apiBase = getRequiredEnv('VITE_API_BASE')
  const workspace = getRequiredEnv('VITE_WORKSPACE')
  const initiative = getRequiredEnv('VITE_INITIATIVE')
  const logoSrc = getRequiredEnv('VITE_LOGO_SRC')
  const siteDescription = getRequiredEnv('VITE_SITE_DESCRIPTION')
  const siteKeywords = getRequiredEnv('VITE_SITE_KEYWORDS')
  const mainSiteDomain = getRequiredEnv('VITE_MAIN_SITE_LINK')

  // Fixed values
  const siteSection = 'Events' // Always 'Events' for this app

  const config: SiteConfig = {
    siteName,
    siteSection,
    fullTitle: `${siteName} - ${siteSection}`,
    api: {
      base: apiBase,
      workspace: workspace,
      initiative: initiative
    },
    logo: {
      src: logoSrc,
      alt: `${siteName} Logo`,
      width: 75, // Fixed dimensions
      height: 50
    },
    meta: {
      description: siteDescription,
      keywords: parseKeywords(siteKeywords),
      ogImage: getOptionalEnv('VITE_OG_IMAGE', '/images/og-default.jpg'),
      favicon: getOptionalEnv('VITE_FAVICON', '/favicon.ico')
    },
    footer: {
      copyrightName: getOptionalEnv('VITE_COPYRIGHT_NAME', siteName),
      copyrightLink: `https://${mainSiteDomain}`
    },
    seo: {
      canonicalBase: `https://events.${mainSiteDomain}`,
      mainSiteUrl: `https://${mainSiteDomain}`
    },
    theme: {
      fonts: {
        sans: getOptionalEnv('VITE_FONT_SANS', "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"),
        heading: getOptionalEnv('VITE_FONT_HEADING', "'Poppins', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
      },
      colors: {
        primary: getOptionalEnv('VITE_COLOR_PRIMARY', '#183847'),
        primaryLight: getOptionalEnv('VITE_COLOR_PRIMARY_LIGHT', '#ffffff'),
        primaryDark: getOptionalEnv('VITE_COLOR_PRIMARY_DARK', '#183847'),
        secondary: getOptionalEnv('VITE_COLOR_SECONDARY', '#64748b'),
        accent: getOptionalEnv('VITE_COLOR_ACCENT', '#ff9900'),
        recovery: getOptionalEnv('VITE_COLOR_RECOVERY', '#2e373b')
      },
      backgrounds: {
        primary: getOptionalEnv('VITE_BG_PRIMARY', '#ffffff'),
        secondary: getOptionalEnv('VITE_BG_SECONDARY', '#fffaf2'),
        dark: getOptionalEnv('VITE_BG_DARK', '#183847'),
        gradient: getOptionalEnv('VITE_BG_GRADIENT', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')
      },
      text: {
        primary: getOptionalEnv('VITE_TEXT_PRIMARY', '#1a202c'),
        secondary: getOptionalEnv('VITE_TEXT_SECONDARY', '#4a5568'),
        light: getOptionalEnv('VITE_TEXT_LIGHT', '#ffffff'),
        muted: getOptionalEnv('VITE_TEXT_MUTED', '#718096')
      }
    }
  };

  return config;
}
